/**
 * @author Michael Hasenstein <hasenstein@yahoo.com>
 * @copyright REFINIO GmbH 2018
 * @license CC-BY-NC-SA-2.5; portions MIT License
 * @version 0.0.1
 */
export const versionNodeTypes = ['VersionNodeEdge', 'VersionNodeChange', 'VersionNodeMerge'];
/**
 * Declare the private ONE storage for RECIPES. They describe how objects are built and are used
 * to create the Javascript, JSON and the microdata representations of ONE objects.
 *
 * Note: The examples for each ONE object type recipe are **with newlines and indentation** for
 * readability. The actual output will not have spaces or newlines!
 * @static
 * @type {Recipe[]}
 */
export const CORE_RECIPES = [
    {
        $type$: 'Recipe',
        name: 'Access',
        rule: [
            {
                itemprop: 'object',
                itemtype: {
                    type: 'referenceToObj',
                    allowedTypes: new Set(['*'])
                },
                isId: true
            },
            {
                // Reference to ID hashes of persons
                itemprop: 'person',
                itemtype: {
                    // TODO Change ALL the wrong "bag" to "set", not just here
                    type: 'bag',
                    item: {
                        type: 'referenceToId',
                        allowedTypes: new Set(['Person'])
                    }
                }
            },
            {
                // References to HashGroup hashes (unversioned)
                itemprop: 'hashGroup',
                itemtype: {
                    type: 'bag',
                    item: {
                        type: 'referenceToObj',
                        allowedTypes: new Set(['HashGroup'])
                    }
                }
            },
            {
                // Optional reference to authorizing AccessCertificate
                // Uses '*' since AccessCertificate is defined in trust.abac
                itemprop: 'basedOnCert',
                optional: true,
                itemtype: {
                    type: 'referenceToObj',
                    allowedTypes: new Set(['*'])
                }
            }
        ]
    },
    {
        $type$: 'Recipe',
        name: 'IdAccess',
        rule: [
            {
                itemprop: 'id',
                itemtype: {
                    type: 'referenceToId',
                    allowedTypes: new Set(['*'])
                },
                isId: true
            },
            {
                itemprop: 'person',
                itemtype: {
                    type: 'bag',
                    item: {
                        type: 'referenceToId',
                        allowedTypes: new Set(['Person'])
                    }
                }
            },
            {
                // References to HashGroup hashes (unversioned)
                itemprop: 'hashGroup',
                itemtype: {
                    type: 'bag',
                    item: {
                        type: 'referenceToObj',
                        allowedTypes: new Set(['HashGroup'])
                    }
                }
            },
            {
                // Optional reference to authorizing AccessCertificate
                // Uses '*' since AccessCertificate is defined in trust.abac
                itemprop: 'basedOnCert',
                optional: true,
                itemtype: {
                    type: 'referenceToObj',
                    allowedTypes: new Set(['*'])
                }
            }
        ]
    },
    {
        $type$: 'Recipe',
        name: 'Chum',
        rule: [
            {
                // Every chum is identified by a name.
                // <span itemprop="name">some-name</span>
                itemprop: 'name',
                isId: true
            },
            {
                // Names of the two instances that can exchange data via this chum.
                // <span itemprop="instance">instance name</span>
                itemprop: 'instance',
                isId: true,
                itemtype: {
                    type: 'array',
                    item: {
                        type: 'string'
                    }
                } // always 2
            },
            {
                // Person hash of the two persons who can exchange data via this Chum.
                // Also used to authenticate the remote user.
                // TODO Make it a referenceToIdObj
                // <span itemprop="person">personHash</span>
                itemprop: 'person',
                isId: true,
                itemtype: {
                    type: 'array',
                    item: {
                        type: 'string',
                        regexp: /^(undefined|[0-9a-f]{64})$/
                    }
                } // Our type "SHA256Hash"
            },
            {
                // Milliseconds-since 1/1/1970 timestamp
                // <span itemprop="highestRemoteTimestamp">timestamp</span>
                itemprop: 'highestRemoteTimestamp',
                itemtype: {
                    type: 'integer'
                }
            },
            // The transfer lists cannot be saved as "array of two" (pairs) because they already
            // are arrays (list:'orderedByONE'), and saving them as JSON-encoded strings is not
            // possible since that would prevent the reverse-map updates for the reference links
            // as well as any other hash-link based functionality.
            {
                itemprop: 'AtoBObjects',
                itemtype: {
                    type: 'bag',
                    item: {
                        type: 'referenceToObj',
                        allowedTypes: new Set(['*'])
                    }
                }
            },
            {
                itemprop: 'AtoBIdObjects',
                itemtype: {
                    type: 'bag',
                    item: {
                        type: 'referenceToId',
                        allowedTypes: new Set(['*'])
                    }
                }
            },
            {
                itemprop: 'AtoBBlob',
                itemtype: {
                    type: 'bag',
                    item: {
                        type: 'referenceToBlob'
                    }
                }
            },
            {
                itemprop: 'AtoBClob',
                itemtype: {
                    type: 'bag',
                    item: {
                        type: 'referenceToClob'
                    }
                }
            },
            {
                itemprop: 'BtoAObjects',
                itemtype: {
                    type: 'bag',
                    item: {
                        type: 'referenceToObj',
                        allowedTypes: new Set(['*'])
                    }
                }
            },
            {
                itemprop: 'BtoAIdObjects',
                itemtype: {
                    type: 'bag',
                    item: {
                        type: 'referenceToId',
                        allowedTypes: new Set(['*'])
                    }
                }
            },
            {
                itemprop: 'BtoABlob',
                itemtype: {
                    type: 'bag',
                    item: {
                        type: 'referenceToBlob'
                    }
                }
            },
            {
                itemprop: 'BtoAClob',
                itemtype: {
                    type: 'bag',
                    item: {
                        type: 'referenceToClob'
                    }
                }
            },
            {
                itemprop: 'BtoAExists',
                itemtype: {
                    type: 'integer'
                }
            },
            {
                // For logging purposes: statistical data
                // <span itemprop="statistics">{sent:...,received:...}</span>
                itemprop: 'statistics',
                itemtype: { type: 'stringifiable' },
                optional: true
            },
            {
                // For logging purposes: List of errors raised by the importer or exporter
                // <span itemprop="errors">["..."]</span>
                itemprop: 'errors',
                itemtype: { type: 'stringifiable' },
                optional: true
            }
        ]
    },
    {
        $type$: 'Recipe',
        name: 'HashGroup',
        rule: [
            {
                // Person set - references to Person ID hashes
                // Uses 'set' instead of 'array' to enable reverse map creation
                // This enables determineAccessibleHashes to find HashGroups via getAllEntries(person, 'HashGroup')
                itemprop: 'person',
                itemtype: {
                    type: 'set',
                    item: {
                        type: 'referenceToId',
                        allowedTypes: new Set(['Person'])
                    }
                }
            }
        ]
    },
    {
        $type$: 'Recipe',
        name: 'Group',
        rule: [
            {
                // <span itemprop="name">${name}</a>
                itemprop: 'name',
                isId: true
            },
            {
                itemprop: 'hashGroup',
                itemtype: {
                    type: 'referenceToObj',
                    allowedTypes: new Set(['HashGroup'])
                }
            },
            {
                // NEW: Reference to HashGroup containing participants
                itemprop: 'participants',
                optional: true,
                itemtype: {
                    type: 'referenceToObj',
                    allowedTypes: new Set(['HashGroup'])
                }
            }
        ]
    },
    {
        $type$: 'Recipe',
        name: 'Instance',
        rule: [
            {
                // Every instance is identified by name, which should be unique per user.
                itemprop: 'name',
                isId: true
            },
            {
                // The owner of the app represented by its person ID.
                itemprop: 'owner',
                itemtype: {
                    type: 'referenceToId',
                    allowedTypes: new Set(['Person'])
                },
                isId: true
            },
            {
                // Reference to Recipe objects.
                itemprop: 'recipe',
                itemtype: {
                    type: 'set',
                    item: {
                        type: 'referenceToObj',
                        allowedTypes: new Set(['Recipe'])
                    }
                }
            },
            {
                itemprop: 'enabledReverseMapTypes',
                itemtype: {
                    type: 'map',
                    key: {
                        type: 'string'
                    },
                    value: {
                        type: 'set',
                        item: {
                            type: 'string'
                        }
                    }
                }
            },
            {
                itemprop: 'enabledReverseMapTypesForIdObjects',
                itemtype: {
                    type: 'map',
                    key: {
                        type: 'string'
                    },
                    value: {
                        type: 'set',
                        item: {
                            type: 'string'
                        }
                    }
                }
            }
        ]
    },
    {
        $type$: 'Recipe',
        name: 'Keys',
        rule: [
            {
                itemprop: 'owner',
                itemtype: {
                    type: 'referenceToId',
                    allowedTypes: new Set(['Instance', 'Person'])
                }
            },
            {
                // ENCRYPTION KEY: Hex encoded public key (64 characters)
                itemprop: 'publicKey',
                // TweetNaCl length is n=32 bytes
                itemtype: {
                    type: 'string',
                    regexp: /^[A-Za-z0-9+/]{64}$/
                }
            },
            {
                // SIGN KEY: Hex encoded public key (64 characters)
                itemprop: 'publicSignKey',
                // TweetNaCl length is n=32 bytes
                itemtype: {
                    type: 'string',
                    regexp: /^[A-Za-z0-9+/]{64}$/
                }
            }
        ]
    },
    {
        $type$: 'Recipe',
        name: 'Person',
        rule: [
            {
                itemprop: 'email',
                isId: true
            },
            {
                itemprop: 'name',
                optional: true
            }
        ]
    },
    {
        $type$: 'Recipe',
        name: 'Recipe', // This recipe describes _itself_ :-)
        rule: [
            {
                // <span itemprop="name">${name}</span>
                itemprop: 'name',
                isId: true
            },
            {
                itemprop: 'crdtConfig',
                optional: true,
                itemtype: {
                    type: 'map',
                    key: { type: 'string' },
                    value: { type: 'string' }
                }
            },
            {
                itemprop: 'rule',
                itemtype: {
                    type: 'array',
                    item: {
                        type: 'object',
                        rules: [
                            {
                                // <span itemprop="itemprop">${itemprop}</span>
                                itemprop: 'itemprop'
                            },
                            {
                                itemprop: 'optional',
                                itemtype: {
                                    type: 'boolean'
                                },
                                optional: true
                            },
                            {
                                // <span itemprop="isId">${true}</span>
                                itemprop: 'isId',
                                itemtype: {
                                    type: 'boolean'
                                },
                                optional: true
                            },
                            {
                                itemprop: 'itemtype',
                                itemtype: { type: 'stringifiable' },
                                optional: true
                            },
                            {
                                itemprop: 'inheritFrom',
                                itemtype: {
                                    type: 'stringifiable'
                                },
                                optional: true
                            }
                        ]
                    }
                }
            }
        ]
    },
    {
        $type$: 'Recipe',
        name: 'VersionNodeChange',
        rule: [
            {
                itemprop: 'prev',
                itemtype: {
                    type: 'referenceToObj',
                    allowedTypes: new Set([
                        'VersionNodeEdge',
                        'VersionNodeChange',
                        'VersionNodeMerge'
                    ])
                },
                optional: true
            },
            {
                itemprop: 'data',
                itemtype: {
                    type: 'referenceToObj',
                    allowedTypes: new Set(['*'])
                }
            },
            {
                itemprop: 'depth',
                itemtype: {
                    type: 'number'
                }
            },
            {
                itemprop: 'creationTime',
                itemtype: {
                    type: 'number'
                }
            }
        ]
    },
    {
        $type$: 'Recipe',
        name: 'VersionNodeEdge',
        rule: [
            {
                itemprop: 'data',
                itemtype: {
                    type: 'referenceToObj',
                    allowedTypes: new Set(['*'])
                }
            },
            {
                itemprop: 'depth',
                itemtype: {
                    type: 'number'
                }
            },
            {
                itemprop: 'creationTime',
                itemtype: {
                    type: 'number'
                }
            }
        ]
    },
    {
        $type$: 'Recipe',
        name: 'VersionNodeMerge',
        rule: [
            {
                itemprop: 'nodes',
                itemtype: {
                    type: 'set',
                    item: {
                        type: 'referenceToObj',
                        allowedTypes: new Set([
                            'VersionNodeEdge',
                            'VersionNodeChange',
                            'VersionNodeMerge'
                        ])
                    }
                }
            },
            {
                itemprop: 'data',
                itemtype: {
                    type: 'referenceToObj',
                    allowedTypes: new Set(['*'])
                }
            },
            {
                itemprop: 'depth',
                itemtype: {
                    type: 'number'
                }
            },
            {
                itemprop: 'creationTime',
                itemtype: {
                    type: 'number'
                }
            }
        ]
    }
];
//# sourceMappingURL=recipes.js.map